import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Fade,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  CheckCircle as AwardedIcon,
  Visibility as ShownIcon,
  Cancel as CancelledIcon,
} from '@mui/icons-material';
import io from 'socket.io-client';
import { API_BASE, authHeaders, prizeValueLabel } from './prizePaletteShared';

const STATUSES = [
  { id: 'all', label: 'All', icon: null },
  { id: 'shown', label: 'Shown', icon: <ShownIcon sx={{ fontSize: 14 }} /> },
  { id: 'awarded', label: 'Awarded', icon: <AwardedIcon sx={{ fontSize: 14 }} /> },
  { id: 'cancelled', label: 'Cancelled', icon: <CancelledIcon sx={{ fontSize: 14 }} /> },
];

const statusChip = (status) => {
  const map = {
    awarded: { color: '#81C784', bg: 'rgba(129,199,132,0.12)', label: 'Awarded', icon: <AwardedIcon sx={{ fontSize: 13 }} /> },
    shown:   { color: '#FFD54F', bg: 'rgba(255,213,79,0.12)', label: 'Shown', icon: <ShownIcon sx={{ fontSize: 13 }} /> },
    cancelled: { color: '#999', bg: 'rgba(150,150,150,0.1)', label: 'Cancelled', icon: <CancelledIcon sx={{ fontSize: 13 }} /> },
  };
  const s = map[status] || map.cancelled;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.35, borderRadius: 999, bgcolor: s.bg }}>
      {s.icon}
      <Typography sx={{ color: s.color, fontWeight: 700, fontSize: 12, textTransform: 'capitalize' }}>{s.label}</Typography>
    </Box>
  );
};

const formatWhen = (award) => {
  const raw = award.awarded_at || award.shown_at || award.created_at;
  if (!raw) return '';
  const date = new Date(raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString();
};

const awardIdentifier = (award) => {
  const type = String(award.user_type || '').toLowerCase();
  const guestId = award.player_guest_id || award.guest_name || '';
  if (type === 'guest' || (!type && guestId)) return guestId || '—';
  return award.player_email || '—';
};

const PrizeHistory = () => {
  const [awards, setAwards] = useState([]);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (status && status !== 'all') params.set('status', status);
      if (search.trim()) params.set('search', search.trim());
      const res = await fetch(`${API_BASE}/api/prize-palette/awards?${params.toString()}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) { setError(data.error || 'Failed to load award history'); return; }
      setAwards(data.awards || []);
      setError(null);
    } catch (err) { console.error('load awards', err); setError('Failed to load award history'); }
    finally { setLoading(false); }
  }, [status, search]);

  useEffect(() => {
    setLoading(true);
    const handle = setTimeout(load, 200);
    return () => clearTimeout(handle);
  }, [load]);

  useEffect(() => {
    const socket = io(API_BASE, { withCredentials: true });
    socket.on('connect', () => socket.emit('prize:admin-join'));
    socket.on('prize:changed', () => load());
    return () => { socket.emit('prize:admin-leave'); socket.disconnect(); };
  }, [load]);

  return (
    <Box>
      {/* filters */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2.5, alignItems: 'center' }}>
        {STATUSES.map((item) => (
          <Chip key={item.id} icon={item.icon} label={item.label} size="small"
            onClick={() => setStatus(item.id)}
            sx={{
              fontWeight: 700, fontSize: 12,
              bgcolor: status === item.id ? '#FFD54F' : '#1e1e1e',
              color: status === item.id ? '#111' : '#aaa',
              border: status === item.id ? 'none' : '1px solid #333',
              '& .MuiChip-icon': { color: status === item.id ? '#111' : '#888' },
            }} />
        ))}
        <TextField size="small" placeholder="Search player, ID, prize, or show…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, ml: { sm: 'auto' },
            '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#1a1a1a', '& fieldset': { borderColor: '#2a2a2a' }, '&:hover fieldset': { borderColor: '#444' } },
          }}
          InputProps={{ sx: { color: '#fff', fontSize: 13, height: 34 } }} />
      </Box>

      {error && (
        <Fade in><Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert></Fade>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#FFD54F' }} size={36} />
        </Box>
      ) : awards.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: '#141414', border: '1px solid #2a2a2a', borderRadius: 3 }}>
          <TrophyIcon sx={{ fontSize: 44, color: '#333', mb: 1 }} />
          <Typography sx={{ color: '#555', fontWeight: 600 }}>No awards yet</Typography>
        </Paper>
      ) : (
        <Paper sx={{ bgcolor: '#141414', border: '1px solid #2a2a2a', borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { borderColor: '#222', py: 1.5 } }}>
                  {['When', 'Player', 'ID', 'Prize', 'Value', 'Session', 'Show', 'Status'].map((h) => (
                    <TableCell key={h} sx={{ color: '#888', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {awards.map((award, i) => (
                  <Fade in key={award.id} timeout={200 + i * 30}>
                    <TableRow sx={{
                      transition: 'background-color 0.15s ease',
                      '&:hover': { bgcolor: '#1a1a1a' },
                      '& td': { borderColor: '#1e1e1e', py: 1.25 },
                    }}>
                      <TableCell sx={{ color: '#888', whiteSpace: 'nowrap', fontSize: 12 }}>{formatWhen(award)}</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{award.player_name || 'Player'}</TableCell>
                      <TableCell sx={{ color: '#888', fontSize: 12 }}>{awardIdentifier(award)}</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>
                        {award.prize_name}
                        {award.wheel_result_text ? (
                          <Typography component="span" sx={{ color: '#CE93D8', fontSize: 11, ml: 0.5 }}>
                            → {award.wheel_result_text}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ color: '#FFD54F', fontWeight: 700, fontSize: 13 }}>{prizeValueLabel(award)}</TableCell>
                      <TableCell sx={{ color: '#555', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11 }}>{award.session_id}</TableCell>
                      <TableCell sx={{ color: '#888', fontSize: 12 }}>{award.show_time_name || '—'}</TableCell>
                      <TableCell>{statusChip(award.status)}</TableCell>
                    </TableRow>
                  </Fade>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default PrizeHistory;
